import { useEffect, useState } from "react";
import { BusyLabel, HitSectionLoading, useLoadingLevel } from "@/components/loading";
import { AdminOrderControls } from "@/components/AdminOrderControls";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { api, publicImageUrl, cardImageUrl, cardName } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useDialog } from "@/lib/dialog";
import { useLightbox } from "@/lib/lightbox";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { BankSelect } from "@/components/BankSelect";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { AuthImg } from "@/components/AuthImg";
import { toast } from "sonner";
import { ArrowLeft, Upload, AlertTriangle, Star, Clock, XCircle, Phone, CreditCard, Landmark, Copy, Check, FileText } from "lucide-react";
import { WaButton } from "@/components/WaButton";
import { OrderRateButton } from "@/components/OrderRateButton";
import { LedgerButtons } from "@/components/LedgerButtons";
import { PaymentMethodBadge } from "@/components/PaymentMethodBadge";
import { CopyableField } from "@/components/CopyableField";
import { ygoRarityLabel } from "@/lib/rarityTier";


const STATUS_LABEL = {
  PENDIENTE_PAGO: "Pendiente de pago", PAGO_POR_VERIFICAR: "En revisión", PAGADO: "Pagado · Retenido",
  ENVIADO: "Enviado", ENTREGADO: "Entregado", PENDIENTE_LIBERACION: "Pendiente de liberación", LIBERADO: "Liberado (cerrado)",
  EN_DISPUTA: "En disputa", REEMBOLSADO: "Reembolsado", RESUELTA_PARCIAL: "Resuelto parcial",
  REEMBOLSO_PENDIENTE: "Reembolso pendiente",
  CANCELADO: "Cancelado",
};

async function upload(file, scope, kind) {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("scope", scope);
  fd.append("kind", kind);
  const r = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return r.data.path;
}

export default function OrderDetail() {
  const { orderId } = useParams();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const viewAs = searchParams.get("view") || "";
  const [o, setO] = useState(null);
  const [loading, setLoading] = useState(true);
  const pageLevel = useLoadingLevel(loading);
  const [contact, setContact] = useState(null);
  const [shippingAudit, setShippingAudit] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/orders/${orderId}`);
      setO(r.data);
      try {
        const c = await api.get(`/orders/${orderId}/contact`);
        setContact(c.data);
      } catch {/* contact reveal not allowed yet */}
    } catch { setO(null); } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [orderId]);

  // Admin-only: check if this order is affected by the shipping-in-net_seller
  // legacy bug (see /api/admin/orders/shipping-missing-report). READ-ONLY.
  useEffect(() => {
    if (user?.role !== "admin" || !orderId) { setShippingAudit(null); return; }
    let cancelled = false;
    api.get(`/admin/orders/${orderId}/shipping-audit`)
      .then(r => { if (!cancelled) setShippingAudit(r.data); })
      .catch(() => { if (!cancelled) setShippingAudit(null); });
    return () => { cancelled = true; };
  }, [orderId, user?.role]);

  if (loading || pageLevel !== "none") return <div className="max-w-4xl mx-auto px-5 py-8 min-h-[70vh]"><HitSectionLoading level={pageLevel} loading={loading} section="inventory" /></div>;
  if (!o) return <div className="max-w-4xl mx-auto p-8 text-neutral-500">Pedido no encontrado</div>;

  const isAdminReal = user?.role === "admin";
  // Admin can force a viewpoint via ?view=buyer|seller (demo pedidos)
  const isBuyer = viewAs === "buyer" ? true : viewAs === "seller" ? false : user?.id === o.buyer_id;
  const isSeller = viewAs === "seller" ? true : viewAs === "buyer" ? false : user?.id === o.seller_id;
  const isAdmin = viewAs ? false : isAdminReal;

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <Link to={isBuyer ? "/buyer" : "/seller"} className="inline-flex items-center gap-2 text-neutral-400 hover:text-white mb-4 text-sm">
        <ArrowLeft size={16} /> Volver
      </Link>

      <div className="flex items-center gap-3 flex-wrap mb-6">
        <h1 className="font-display text-3xl">Pedido #{o.id.slice(0, 8)}</h1>
        <span className={`status-pill status-${o.status.toLowerCase().replace("_", "-")}`}>{STATUS_LABEL[o.status] || o.status}</span>
        {/* Método de pago visible para todos (comprador, vendedor, admin).
            Feb 2026 — así, cuando el fee Payphone es $0, queda claro que es
            porque fue transferencia y no un bug. */}
        <PaymentMethodBadge method={o.payment_method} />
        {o.high_value_flag && <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/30"><AlertTriangle size={12} className="mr-1" />Alto valor</Badge>}
        {o.is_demo && <Badge className="bg-purple-500/20 text-purple-300 border-purple-500/30">DEMO</Badge>}
      </div>

      {isAdminReal && (
        <div className="mb-4 flex items-center gap-2 p-3 bg-[#111] border border-[#EF4444]/30 rounded-md">
          <span className="text-xs text-neutral-400 mr-2">Ver como:</span>
          <button
            data-testid="viewas-admin"
            onClick={() => setSearchParams({})}
            className={`px-3 py-1 text-xs rounded ${!viewAs ? "bg-[#EF4444] text-white" : "text-neutral-400 border border-[#262626]"}`}
          >Admin</button>
          <button
            data-testid="viewas-buyer"
            onClick={() => setSearchParams({ view: "buyer" })}
            className={`px-3 py-1 text-xs rounded ${viewAs === "buyer" ? "bg-blue-500 text-white" : "text-neutral-400 border border-[#262626]"}`}
          >Comprador</button>
          <button
            data-testid="viewas-seller"
            onClick={() => setSearchParams({ view: "seller" })}
            className={`px-3 py-1 text-xs rounded ${viewAs === "seller" ? "bg-emerald-500 text-white" : "text-neutral-400 border border-[#262626]"}`}
          >Vendedor</button>
        </div>
      )}

      {/* Feb 2026 — Admin-only yellow banner cuando la orden tiene el bug
          de envío faltante en `net_seller`. NO cambia el número mostrado
          más abajo (el display sigue fiel a la DB) — solo avisa al admin
          para que consulte el reporte antes de pagar. */}
      {isAdminReal && shippingAudit?.affected && (
        <div
          className="mb-4 p-4 bg-amber-500/10 border border-amber-500/40 rounded-md flex items-start gap-3"
          data-testid="shipping-audit-banner"
        >
          <AlertTriangle size={18} className="text-amber-400 mt-0.5 flex-none" />
          <div className="flex-1 text-sm text-amber-100 leading-relaxed">
            <div className="font-bold text-amber-200 mb-1">Orden afectada por el bug de envío pre-Feb 2026</div>
            El <code className="text-amber-300">net_seller</code> guardado (<b>${shippingAudit.net_seller_stored.toFixed(2)}</b>) no incluye el envío. El neto correcto es <b>${shippingAudit.net_seller_correct.toFixed(2)}</b> — al vendedor le faltan <b className="text-[#EF4444]">${shippingAudit.delta_owed.toFixed(2)}</b>{shippingAudit.already_released_to_seller ? " que ya no puedes ajustar en esta orden (págalos aparte)" : " que puedes sumarle a la liberación"}. Antes de pagar, revisa el{" "}
            <Link to="/admin/shipping-audit" className="text-amber-300 underline hover:text-amber-100" data-testid="shipping-audit-banner-link">reporte de auditoría de envíos</Link>.
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Main */}
        <div className="md:col-span-2 space-y-6">
          {/* Timeline */}
          <div className="bg-[#111] border border-[#262626] rounded-md p-6">
            <h2 className="font-display text-xl mb-4">Estado del pedido</h2>
            <div className="space-y-3 text-sm">
              <TimelineStep label="Pagado" date={o.paid_at} active={!!o.paid_at} extra={o.paid_at ? <PaymentMethodBadge method={o.payment_method} size="sm" /> : null} />
              <TimelineStep label="Enviado" date={o.shipped_at} active={!!o.shipped_at} />
              <TimelineStep label="Entregado" date={o.delivered_at} active={!!o.delivered_at} />
              <TimelineStep label="Liberado (pago al vendedor)" date={o.released_at} active={!!o.released_at} />
            </div>
          </div>

          {/* Card info — all items in this order */}
          <div className="bg-[#111] border border-[#262626] rounded-md p-6">
            <h2 className="font-display text-xl mb-4">
              {o.item_count > 1 ? `${o.item_count} cartas en este pedido` : "Carta"}
            </h2>
            <div className="space-y-3">
              {(o.items || []).map((it, idx) => (
                <div key={it.publication_id || idx} className="flex gap-4 pb-3 border-b border-[#171717] last:border-b-0 last:pb-0" data-testid={`order-item-${idx}`}>
                  {it.card?.image_url && <img src={cardImageUrl(it.card.image_url)} alt="" className="w-20 h-28 object-contain flex-shrink-0" onError={(e) => e.currentTarget.style.display = "none"} />}
                  <div className="flex-1 min-w-0">
                    <div className="font-medium">{cardName(it.card, it.publication_language)}</div>
                    <div className="text-neutral-500 text-sm">{it.publication_snapshot?.yugioh_set_code
                      ? `${it.publication_snapshot.yugioh_set_name || ""} · ${it.publication_snapshot.yugioh_set_code} · ${ygoRarityLabel(it.publication_snapshot)}`
                      : it.card?.set_name}</div>
                    <div className="text-neutral-500 text-xs mt-1">Idioma: {(it.publication_language || "").toUpperCase()} · Condición: {it.publication_snapshot?.condition}</div>
                    <div className="text-neutral-500 text-xs">Cantidad: <b className="text-white">{it.quantity}</b> · Precio: <b className="text-white">${Number(it.unit_price ?? (it.price / Math.max(1, it.quantity))).toFixed(2)}</b> c/u</div>
                  </div>
                  <div className="text-right text-sm">
                    <div className="text-neutral-500 text-xs">Subtotal</div>
                    <div className="font-display text-lg text-white">${Number(it.buyer_pays_line || it.price).toFixed(2)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Seller shipping / meetup delivery */}
          {(isBuyer || isSeller) && ["LIBERADO", "RESUELTA_PARCIAL"].includes(o.status) && <OrderRateButton orderId={o.id} />}
          {isSeller && o.status === "PAGADO" && (
            <>
              {o.delivery_method === "meetup"
                ? <SellerMeetupForm orderId={o.id} reload={load} />
                : <SellerShipForm orderId={o.id} reload={load} />}
              <SellerCannotFulfillBlock orderId={o.id} reload={load} />
              <ShipExtensionBlock order={o} isBuyer={isBuyer} isSeller={isSeller} reload={load} />
            </>
          )}
          {isBuyer && o.status === "PAGADO" && (
            <ShipExtensionBlock order={o} isBuyer={isBuyer} isSeller={isSeller} reload={load} />
          )}

          {/* Ship / meetup deadline timer for buyer + seller */}
          {(isBuyer || isSeller) && o.status === "PAGADO" && o.ship_deadline_at && <ShipTimer order={o} role={isBuyer ? "buyer" : "seller"} onRefund={load} />}

          {/* Release countdown (post-shipment) for both parties */}
          {(isBuyer || isSeller) && o.status === "ENVIADO" && o.release_target_at && <ReleaseTimer order={o} role={isBuyer ? "buyer" : "seller"} onReload={load} />}

          {/* Pending payment actions — buyer can resume or cancel.
              Sub-flujo transferencia bancaria: si el order es transfer,
              en vez del botón "Continuar en Payphone" mostramos el form
              de comprobante + los datos de la cuenta HitTCG. */}
          {isBuyer && o.status === "PENDIENTE_PAGO" && o.auction_id && !o.payment_method && (
            <Link to={`/subastas/pagar/${o.auction_id}`} data-testid="order-auction-pay-link"
                  className="block text-center bg-[#EF4444] hover:bg-[#DC2626] text-white font-bold rounded-md px-4 py-3">
              Completar el pago de tu subasta
            </Link>
          )}
          {isBuyer && o.status === "PENDIENTE_PAGO" && !(o.auction_id && !o.payment_method) && (
            o.payment_method === "transfer"
              ? <TransferReceiptForm order={o} reload={load} />
              : <PendingPaymentActions order={o} reload={load} />
          )}

          {/* Estado intermedio: comprobante enviado, admin aún no ha
              cotejado con su banco. Visible a comprador + vendedor. */}
          {(isBuyer || isSeller) && o.status === "PAGO_POR_VERIFICAR" && (
            <TransferVerifyingBanner order={o} isBuyer={isBuyer} reload={load} />
          )}

          {/* Manual refund flow — buyer bank info + status tracking */}
          {o.status === "REEMBOLSO_PENDIENTE" && (
            <RefundPendingBlock order={o} isBuyer={isBuyer} reload={load} />
          )}
          {o.status === "REEMBOLSADO" && o.refund_method && (
            <RefundCompletedBanner order={o} isBuyer={isBuyer} />
          )}

          {/* Contact info revealed post-purchase */}
          {contact && o.status !== "PENDIENTE_PAGO" && (() => {
            // Backend returns { buyer, seller, delivery_method, ... }. Pick the counterparty.
            const other = isBuyer ? contact.seller : isSeller ? contact.buyer : null;
            if (!other) return null;
            const title = isBuyer ? "Contacto del vendedor" : "Datos del comprador";
            return (
              <div className="bg-[#111] border border-[#262626] rounded-md p-6" data-testid="order-contact-card">
                <h2 className="font-display text-xl mb-2 flex items-center gap-2" data-testid="contact-title"><Phone size={18} /> {title}</h2>
                <div className="text-sm text-neutral-300 space-y-1">
                  <div><b className="text-white" data-testid="contact-name">{other.name}</b>{other.city ? ` · ${other.city}` : ""}{other.province ? `, ${other.province}` : ""}</div>
                  {other.phone && <div>📞 <a href={`tel:${other.phone}`} className="text-[#EF4444]" data-testid="contact-phone">{other.phone}</a>
                    <WaButton phone={other.phone} testid="contact-whatsapp" /></div>}
                  {other.email && <div>✉️ <a href={`mailto:${other.email}`} className="text-[#EF4444]" data-testid="contact-email">{other.email}</a></div>}
                </div>
              </div>
            );
          })()}

          {isAdminReal && o.auction_id && contact && (
            <div className="bg-[#111] border border-[#262626] rounded-md p-6" data-testid="admin-auction-contact-card">
              <h2 className="font-display text-xl mb-2 flex items-center gap-2"><Phone size={18} /> Contacto de las partes (subasta)</h2>
              <div className="text-sm text-neutral-300 space-y-1">
                {[["Vendedor", contact.seller, "seller"], ["Comprador", contact.buyer, "buyer"]].map(([label, p, k]) => (
                  <div key={k} data-testid={`admin-contact-${k}`}>
                    <b className="text-white">{label}:</b> {p?.name || "—"} · {p?.phone
                      ? <>
                          <a href={`tel:${p.phone}`} className="text-[#EF4444]" data-testid={`admin-contact-${k}-phone`}>{p.phone}</a>
                          <WaButton phone={p.phone} testid={`admin-contact-${k}-whatsapp`} />
                        </>
                      : <span className="text-neutral-500">sin teléfono registrado</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Guide info (visible when shipped) */}
          {o.guide_number && (
            <div className="bg-[#111] border border-[#262626] rounded-md p-6">
              <h2 className="font-display text-xl mb-4">
                {o.delivery_method === "meetup" ? "Evidencia de entrega en encuentro" : "Guía de envío"}
              </h2>
              <div className="text-sm space-y-1">
                {o.delivery_method !== "meetup" && (
                  <div><span className="text-neutral-500">Número:</span> <b className="text-white">{o.guide_number}</b></div>
                )}
                {o.guide_photo_path && (isBuyer || isSeller || isAdmin) && (
                  <div className="mt-3">
                    <div className="text-xs text-neutral-500 mb-1">Foto del vendedor</div>
                    <AuthImg path={o.guide_photo_path} width={600} className="max-w-xs rounded border border-[#262626]" alt="Foto de entrega" />
                  </div>
                )}
                {o.buyer_delivery_photo_path && (isBuyer || isSeller || isAdmin) && (
                  <div className="mt-3">
                    <div className="text-xs text-neutral-500 mb-1">Foto del comprador</div>
                    <AuthImg path={o.buyer_delivery_photo_path} width={600} className="max-w-xs rounded border border-[#262626]" alt="Foto del comprador" />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Buyer confirm */}
          {isBuyer && o.status === "ENVIADO" && (
            <BuyerConfirmForm order={o} reload={load} />
          )}

          {/* Rate */}
          {/* Rating moved to a global modal on Home per UX spec — no per-order form here. */}

          {/* Dispute view */}
          {o.dispute && <DisputeView order={o} isSeller={isSeller} reload={load} />}

          {/* Ledger docs — visible for buyer/seller/admin of the order */}
          <LedgerButtons orderId={o.id} paymentMethod={o.payment_method} />

          {/* Admin actions */}
          {isAdmin && <AdminActions order={o} reload={load} />}
        </div>

        {/* Sidebar */}
        <div className="bg-[#111] border border-[#262626] rounded-md p-6 h-fit">
          <div className="text-neutral-500 text-xs uppercase tracking-wider mb-3">Resumen financiero</div>
          <div className="space-y-2 text-sm">
            {/* Método de pago — visible para comprador, vendedor y admin.
                Feb 2026: si `payphone_fee = 0` por ser transferencia, este
                badge explica por qué; sin él parece un bug de cálculo. */}
            {o.payment_method && (
              <div className="flex items-center justify-between pb-2 border-b border-[#262626]" data-testid="order-payment-method-row">
                <span className="text-neutral-400">Método de pago</span>
                <PaymentMethodBadge method={o.payment_method} size="sm" />
              </div>
            )}
            <Row label="Precio carta" value={`$${Number(o.price || 0).toFixed(2)}`} />
            <Row label="Envío" value={`$${Number(o.shipping_cost || 0).toFixed(2)}`} />
            <div className="border-t border-[#262626] pt-2">
              <Row label={isBuyer ? (["PENDIENTE_PAGO", "PAGO_POR_VERIFICAR"].includes(o.status) ? "TOTAL A PAGAR" : "TOTAL PAGADO") : "Bruto"} value={`$${Number(o.total || 0).toFixed(2)}`} big />
            </div>
            {(isSeller || isAdmin) && (
              <>
                <Row label="Comisión HitTCG" value={`-$${Number(o.commission || 0).toFixed(2)}`} negative />
                {typeof o.payphone_fee === "number" && o.payphone_fee > 0 && (
                  <Row label="Comisión Payphone (5.75% s/ precio + envío)" value={`-$${Number(o.payphone_fee || 0).toFixed(2)}`} negative />
                )}
                {typeof o.payphone_fee === "number" && o.payphone_fee === 0 && o.payment_method === "transfer" && (
                  <div className="text-[11px] text-blue-300/80 italic" data-testid="order-no-payphone-fee-note">
                    Sin comisión Payphone (pago por transferencia bancaria).
                  </div>
                )}
                <div className="border-t border-[#262626] pt-2">
                  <Row label="NETO VENDEDOR (incluye envío)" value={`$${Number(o.net_seller || 0).toFixed(2)}`} big />
                </div>
              </>
            )}
          </div>
          <div className="mt-6 pt-4 border-t border-[#262626] text-xs space-y-1 text-neutral-500">
            <div>Comprador: <b className="text-white">{o.buyer?.name}</b></div>
            <div>Vendedor: <b className="text-white">{o.seller?.name}</b></div>
            <div>Ciudad envío: {o.buyer?.city}</div>
            <div className="pt-2 space-y-0.5">
              {o.delivery_method === "meetup" ? (
                <div className="text-neutral-500 italic">Entrega en encuentro presencial · sin datos de envío</div>
              ) : typeof o.shipping_address === "string" ? (
                <div className="text-neutral-600 whitespace-pre-line">{o.shipping_address}</div>
              ) : o.shipping_address ? (
                <>
                  <div className="text-white"><b>Receptor:</b> {o.shipping_address.receiver_name}</div>
                  <div><b>Cédula/RUC:</b> {o.shipping_address.receiver_cedula}</div>
                  <div><b>Dirección:</b> {o.shipping_address.address_line}</div>
                  <div><b>Ciudad:</b> {o.shipping_address.city}</div>
                  {o.shipping_address.reference && <div><b>Ref:</b> {o.shipping_address.reference}</div>}
                  <div><b>Tel:</b> {o.shipping_address.phone}</div>
                </>
              ) : null}
            </div>
            {o.cancel_reason && (
              <div className="mt-2 p-2 bg-amber-500/10 border border-amber-500/30 rounded text-amber-300" data-testid="order-cancel-reason">
                <b>Cancelado</b>{o.cancelled_at ? ` · ${new Date(o.cancelled_at).toLocaleString("es-EC")}` : ""}: {o.cancel_reason}
              </div>
            )}
            {o.refund_reason && (
              <div className="mt-2 p-2 bg-red-500/10 border border-red-500/30 rounded text-red-300" data-testid="order-refund-reason">
                <b>Reembolso</b>{o.refunded_at ? ` · ${new Date(o.refunded_at).toLocaleString("es-EC")}` : ""}: {o.refund_reason}
              </div>
            )}
            {o.dispute?.transit_no_fault && (
              <div className="mt-2 p-3 bg-blue-500/10 border border-blue-500/30 rounded" data-testid="order-transit-info">
                <div className="text-blue-300 font-semibold text-sm mb-1">Cerrado por daño en tránsito (courier)</div>
                <div className="text-xs text-neutral-300">
                  El daño ocurrió en manos del courier. Bajo esta resolución <b className="text-white">nadie se reembolsa</b>: el vendedor cobra su pago normalmente (envió una carta en buen estado) y el comprador no recibe reembolso porque la responsabilidad es del courier. Sin strikes para ninguna de las partes.
                </div>
              </div>
            )}
            {o.delivery_method === "meetup" && (
              <div className="mt-3 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded" data-testid="order-meetup-block">
                <div className="text-emerald-300 font-semibold text-sm mb-1 flex items-center gap-1"><Phone size={12} /> Entrega en encuentro presencial</div>
                {o.meetup_point && (
                  <div className="text-neutral-100 text-xs mb-2"><b>Punto acordado:</b> {o.meetup_point}</div>
                )}
                {o.meetup_notes && <div className="text-neutral-400 text-xs mb-2"><b>Sugerencia del vendedor:</b> {o.meetup_notes}</div>}
                {contact ? (
                  <div className="text-xs space-y-1">
                    <div><b className="text-white">Contacto del vendedor:</b> {contact.seller?.name} · <a href={`tel:${contact.seller?.phone}`} className="text-emerald-300">{contact.seller?.phone}</a>{contact.seller?.phone && <WaButton phone={contact.seller.phone} testid="meetup-seller-whatsapp" />}</div>
                    <div><b className="text-white">Contacto del comprador:</b> {contact.buyer?.name} · <a href={`tel:${contact.buyer?.phone}`} className="text-emerald-300">{contact.buyer?.phone}</a>{contact.buyer?.phone && <WaButton phone={contact.buyer.phone} testid="meetup-buyer-whatsapp" />}</div>
                  </div>
                ) : (
                  <div className="text-xs text-neutral-400">Los datos de contacto se revelan después del pago.</div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, big, negative }) {
  return (
    <div className="flex justify-between items-baseline">
      <span className={big ? "font-display text-white text-sm" : "text-neutral-400"}>{label}</span>
      <span className={`${big ? "font-display text-2xl text-[#EF4444]" : negative ? "text-neutral-500" : "text-white"}`}>{value}</span>
    </div>
  );
}

function TimelineStep({ label, date, active, extra }) {
  return (
    <div className="flex items-center gap-3">
      <div className={`w-3 h-3 rounded-full ${active ? "bg-[#EF4444]" : "bg-[#262626]"}`} />
      <div className="flex-1 flex justify-between items-center gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <span className={active ? "text-white" : "text-neutral-500"}>{label}</span>
          {active && extra ? extra : null}
        </div>
        <span className="text-neutral-500 text-xs">{date ? new Date(date).toLocaleString("es-EC") : "—"}</span>
      </div>
    </div>
  );
}

function SellerShipForm({ orderId, reload }) {
  const [guide, setGuide] = useState("");
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Sube la foto de la guía"); return; }
    setLoading(true);
    try {
      const path = await upload(file, "private", "guide");
      await api.post(`/orders/${orderId}/ship`, { guide_number: guide, guide_photo_path: path });
      toast.success("Enviado");
      reload();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); } finally { setLoading(false); }
  };
  return (
    <form onSubmit={submit} className="bg-[#111] border border-[#262626] rounded-md p-6 space-y-4">
      <h2 className="font-display text-xl">Subir guía de envío</h2>
      <div>
        <Label className="text-neutral-400 text-xs uppercase tracking-wider mb-2 block">Número de guía / tracking</Label>
        <Input data-testid="ship-guide-number" value={guide} onChange={(e) => setGuide(e.target.value)} required className="bg-[#171717] border-[#262626]" />
      </div>
      <div>
        <Label className="text-neutral-400 text-xs uppercase tracking-wider mb-2 block">Foto de la guía</Label>
        <label className="flex items-center gap-3 p-4 bg-[#0A0A0A] border border-dashed border-[#262626] rounded-md cursor-pointer hover:border-[#EF4444]">
          <Upload size={18} className="text-neutral-500" />
          <span className="text-sm text-neutral-300 flex-1">{file ? file.name : "Subir foto"}</span>
          <input data-testid="ship-guide-photo" type="file" accept="image/*" onChange={(e) => setFile(e.target.files?.[0])} className="hidden" />
        </label>
      </div>
      <Button data-testid="ship-submit" type="submit" disabled={loading} className="bg-[#EF4444] hover:bg-[#DC2626] w-full">
        <BusyLabel busy={loading} text="Enviando…">Marcar como ENVIADO</BusyLabel>
      </Button>
    </form>
  );
}

function SellerMeetupForm({ orderId, reload }) {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Sube la foto que confirma la entrega en el encuentro"); return; }
    setLoading(true);
    try {
      const path = await upload(file, "private", "meetup");
      await api.post(`/orders/${orderId}/ship`, { guide_number: "", guide_photo_path: path });
      toast.success("Entrega registrada. El comprador ahora debe confirmar.");
      reload();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); } finally { setLoading(false); }
  };
  return (
    <form onSubmit={submit} className="bg-[#111] border border-emerald-500/30 rounded-md p-6 space-y-4">
      <h2 className="font-display text-xl text-emerald-300">Confirmar entrega en encuentro</h2>
      <p className="text-xs text-neutral-400">Coordina el encuentro por teléfono y, al hacer la entrega, sube una foto que confirme que entregaste la carta al comprador.</p>
      <div>
        <Label className="text-neutral-400 text-xs uppercase tracking-wider mb-2 block">Foto de la entrega en el encuentro</Label>
        <label className="flex items-center gap-3 p-4 bg-[#0A0A0A] border border-dashed border-[#262626] rounded-md cursor-pointer hover:border-emerald-500">
          <Upload size={18} className="text-neutral-500" />
          <span className="text-sm text-neutral-300 flex-1">{file ? file.name : "Subir foto de la entrega"}</span>
          <input data-testid="meetup-seller-photo" type="file" accept="image/*" onChange={(e) => setFile(e.target.files?.[0])} className="hidden" />
        </label>
      </div>
      <Button data-testid="meetup-seller-submit" type="submit" disabled={loading} className="bg-emerald-600 hover:bg-emerald-700 w-full">
        <BusyLabel busy={loading} text="Registrando…">Marcar como ENTREGADO EN ENCUENTRO</BusyLabel>
      </Button>
    </form>
  );
}

function PendingPaymentActions({ order, reload }) {
  const { confirm } = useDialog();
  const [busy, setBusy] = useState(null);
  const deadline = order.payment_deadline_at ? new Date(order.payment_deadline_at) : null;

  const resume = async () => {
    setBusy("resume");
    try {
      const r = await api.post(`/orders/${order.id}/resume-payment`);
      window.location.href = r.data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo retomar el pago");
      setBusy(null);
      reload && reload();
    }
  };

  const cancel = async () => {
    const ok = await confirm({
      title: "Cancelar pedido pendiente",
      description: "Se cancelará este pedido y las cartas quedarán disponibles nuevamente en el catálogo. ¿Continuar?",
      confirmLabel: "Sí, cancelar",
      danger: true,
    });
    if (!ok) return;
    setBusy("cancel");
    try {
      await api.post(`/orders/${order.id}/cancel-pending`);
      toast.success("Pedido cancelado. Las cartas vuelven al catálogo.");
      reload && reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo cancelar");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="bg-[#111] border border-amber-500/40 rounded-md p-6 space-y-4" data-testid="order-pending-actions">
      <div className="flex items-center gap-2">
        <Clock size={18} className="text-amber-400" />
        <h2 className="font-display text-xl text-amber-300">Este pedido está pendiente de pago</h2>
      </div>
      <p className="text-sm text-neutral-400">
        Tu compra no se ha completado en Payphone. Puedes retomar el pago o cancelar el pedido para liberar las cartas.
      </p>
      {deadline && (
        <div className="text-xs text-neutral-500">
          Tiempo límite para pagar: <b className="text-amber-300">{deadline.toLocaleString("es-EC")}</b>. Si no completas el pago antes, el pedido se cancelará automáticamente y las cartas volverán al catálogo.
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        <Button
          data-testid="order-resume-pay"
          onClick={resume}
          disabled={busy !== null}
          className="bg-[#EF4444] hover:bg-[#DC2626]"
        >
          <CreditCard size={16} className="mr-2" />
          {busy === "resume" ? "Abriendo Payphone…" : "Continuar con el pago"}
        </Button>
        <Button
          data-testid="order-cancel-pending"
          variant="ghost"
          onClick={cancel}
          disabled={busy !== null}
          className="text-neutral-300 hover:text-[#EF4444]"
        >
          <XCircle size={16} className="mr-2" />
          {busy === "cancel" ? "Cancelando…" : "Cancelar pedido"}
        </Button>
      </div>
    </div>
  );
}


function SellerCannotFulfillBlock({ orderId, reload }) {
  const { confirm } = useDialog();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [hasItems, setHasItems] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const ok = await confirm({
      title: "No puedo cumplir el pedido",
      description:
        hasItems
          ? "Esto reembolsará el pedido completo al comprador y tus cartas volverán a estar a la venta. Recibirás un STRIKE (al tercer strike, la cuenta se banea automáticamente). ¿Continuar?"
          : "Esto reembolsará el pedido completo al comprador, desactivará la(s) publicación(es) involucrada(s) y te sumará un STRIKE (al tercer strike, la cuenta se banea automáticamente). ¿Continuar?",
      confirmLabel: "Sí, no puedo cumplir",
      danger: true,
    });
    if (!ok) return;
    setBusy(true);
    try {
      const r = await api.post(`/orders/${orderId}/seller-cannot-fulfill`, { reason: reason.trim(), still_has_items: hasItems });
      toast.success(
        r.data.refund_method === "manual"
          ? `Reembolso de $${Number(r.data.refunded_amount).toFixed(2)} registrado: HitTCG lo transferirá al comprador. Se te asignó un strike.`
          : `Pedido reembolsado ($${Number(r.data.refunded_amount).toFixed(2)}). Se te asignó un strike y el admin fue notificado.`,
        { duration: 8000 },
      );
      setOpen(false);
      setReason("");
      reload && reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo procesar");
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <div className="bg-[#111] border border-red-500/30 rounded-md p-4" data-testid="seller-cannot-fulfill-block">
        <div className="flex items-start gap-3">
          <AlertTriangle size={18} className="text-red-400 mt-0.5 shrink-0" />
          <div className="flex-1">
            <div className="text-sm font-medium text-red-200">¿No puedes cumplir este pedido?</div>
            <div className="text-[11px] text-neutral-400 mt-1 leading-relaxed">
              Usa esto <b>si perdiste la carta, la vendiste por otro lado o no puedes enviarla</b>. Reembolsaremos al comprador el total pagado y recibirás un <b className="text-red-300">STRIKE</b> (al 3er strike, la cuenta se banea automáticamente). Si todavía tienes la carta, podrás dejarla a la venta.
            </div>
            <Button
              type="button"
              onClick={() => setOpen(true)}
              variant="outline"
              size="sm"
              className="mt-3 border-red-500/40 text-red-300 hover:bg-red-500/10"
              data-testid="seller-cannot-fulfill-btn"
            >
              <XCircle size={14} className="mr-1.5" />
              No puedo cumplir el pedido
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#111] border border-red-500/40 rounded-md p-6 space-y-3" data-testid="seller-cannot-fulfill-form">
      <div className="flex items-center gap-2">
        <AlertTriangle size={18} className="text-red-400" />
        <h2 className="font-display text-lg text-red-200">Confirmar que no puedes cumplir</h2>
      </div>
      <div className="text-xs text-neutral-400 leading-relaxed">
        Vas a reembolsar el pedido completo al comprador. Se te asignará un strike y {hasItems ? "tus cartas volverán a estar a la venta" : "se desactivarán las publicaciones involucradas"}. El admin recibirá una notificación.
      </div>
      <div>
        <Label className="text-neutral-400 text-xs uppercase tracking-wider mb-1.5 block">Motivo (opcional, pero recomendado)</Label>
        <Textarea
          data-testid="seller-cannot-fulfill-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Ej: perdí la carta, ya la había vendido en persona, error de inventario…"
          className="bg-[#171717] border-[#262626] min-h-[80px]"
        />
      </div>
      <label className="flex items-start gap-2 text-xs text-neutral-300" data-testid="seller-cannot-fulfill-has-items">
        <input type="checkbox" checked={hasItems} onChange={(e) => setHasItems(e.target.checked)} className="mt-0.5 accent-[#EF4444]" data-testid="seller-cannot-fulfill-has-items-input" />
        <span>Todavía tengo la(s) carta(s) (por ejemplo, no envío a esa provincia). Vuelven a estar a la venta. Déjalo sin marcar si ya la vendiste o la perdiste.</span>
      </label>
      <div className="flex gap-2">
        <Button
          type="button"
          onClick={submit}
          disabled={busy}
          className="bg-red-600 hover:bg-red-700"
          data-testid="seller-cannot-fulfill-submit"
        >
          <BusyLabel busy={busy} text="Procesando…">Sí, reembolsar y aceptar strike</BusyLabel>
        </Button>
        <Button
          type="button"
          variant="ghost"
          onClick={() => { setOpen(false); setReason(""); }}
          disabled={busy}
          data-testid="seller-cannot-fulfill-cancel"
        >
          Cancelar
        </Button>
      </div>
    </div>
  );
}



function BuyerConfirmForm({ order, reload }) {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const isMeetup = order.delivery_method === "meetup";
  const submit = async () => {
    if (isMeetup && !file) { toast.error("Sube una foto que confirme que recibiste todo bien"); return; }
    setLoading(true);
    try {
      const body = {};
      if (isMeetup) {
        body.photo_path = await upload(file, "private", "meetup");
      }
      await api.post(`/orders/${order.id}/confirm-received`, body);
      toast.success("Recepción confirmada. HitTCG liberará el pago al vendedor cuando registre la transferencia.");
      reload();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); } finally { setLoading(false); }
  };
  return (
    <div className="bg-[#111] border border-[#262626] rounded-md p-6">
      <h2 className="font-display text-xl mb-2">¿Recibiste {isMeetup ? "la carta en el encuentro" : "la carta"}?</h2>
      <p className="text-neutral-400 text-sm mb-4">
        Al confirmar, el pedido queda <b className="text-white">pendiente de liberación</b>. HitTCG revisa el cuadre y transfiere al vendedor.
        {order.release_target_at && <> Si no confirmas ni abres disputa antes del {new Date(order.release_target_at).toLocaleDateString("es-EC")}, se cierra automáticamente y también entra a la cola de pagos.</>}
      </p>
      {isMeetup && (
        <div className="mb-4">
          <Label className="text-neutral-400 text-xs uppercase tracking-wider mb-2 block">Foto que confirma la entrega (obligatoria)</Label>
          <label className="flex items-center gap-3 p-4 bg-[#0A0A0A] border border-dashed border-[#262626] rounded-md cursor-pointer hover:border-emerald-500">
            <Upload size={18} className="text-neutral-500" />
            <span className="text-sm text-neutral-300 flex-1">{file ? file.name : "Subir foto"}</span>
            <input data-testid="meetup-buyer-photo" type="file" accept="image/*" onChange={(e) => setFile(e.target.files?.[0])} className="hidden" />
          </label>
        </div>
      )}
      <div className="flex gap-2 flex-wrap">
        <Button data-testid="order-confirm-received" onClick={submit} disabled={loading} className="bg-emerald-600 hover:bg-emerald-700">
          <BusyLabel busy={loading} text="Guardando…">Confirmar recepción</BusyLabel>
        </Button>
        <DisputeOpener orderId={order.id} onDone={reload} />
      </div>
    </div>
  );
}

function DisputeOpener({ orderId, onDone }) {
  const [open, setOpen] = useState(false);
  const [caseNum, setCase] = useState("2");
  const [reason, setReason] = useState("");
  const [files, setFiles] = useState([]);
  const submit = async (e) => {
    e.preventDefault();
    const paths = [];
    for (const f of files) paths.push(await upload(f, "private", "dispute"));
    await api.post(`/orders/${orderId}/dispute`, {
      case: parseInt(caseNum), reason, photo_paths: paths,
    });
    toast.success("Disputa abierta");
    setOpen(false);
    onDone();
  };
  if (!open) return <Button data-testid="dispute-open" variant="outline" onClick={() => setOpen(true)} className="border-[#EF4444] text-[#EF4444] hover:bg-[#EF4444]/10">Abrir disputa</Button>;
  return (
    <form onSubmit={submit} className="w-full bg-[#0A0A0A] border border-[#EF4444]/30 rounded-md p-4 space-y-3">
      <div className="font-display text-lg">Nueva disputa</div>
      <Select value={caseNum} onValueChange={setCase}>
        <SelectTrigger data-testid="dispute-case" className="bg-[#171717] border-[#262626]"><SelectValue /></SelectTrigger>
        <SelectContent className="bg-[#171717] border-[#262626] text-white">
          <SelectItem value="1">Caso 1 — No fue enviado</SelectItem>
          <SelectItem value="2">Caso 2 — Nunca llegó</SelectItem>
          <SelectItem value="3">Caso 3 — Llegó dañada / no era así</SelectItem>
        </SelectContent>
      </Select>
      <Textarea data-testid="dispute-reason" value={reason} onChange={(e) => setReason(e.target.value)} required placeholder="Explica lo que pasó" className="bg-[#171717] border-[#262626]" />
      <label className="flex items-center gap-2 text-sm text-neutral-400 cursor-pointer">
        <Upload size={16} /> {files.length ? `${files.length} foto(s)` : "Subir fotos"}
        <input data-testid="dispute-photos" type="file" multiple accept="image/*" onChange={(e) => setFiles(Array.from(e.target.files || []))} className="hidden" />
      </label>
      <div className="flex gap-2">
        <Button data-testid="dispute-submit" type="submit" className="bg-[#EF4444] hover:bg-[#DC2626]">Abrir disputa</Button>
        <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button>
      </div>
    </form>
  );
}

function DisputeView({ order, isSeller, reload }) {
  const d = order.dispute;
  const [files, setFiles] = useState([]);
  const [response, setResponse] = useState("");
  const canRespond = isSeller && d.status === "abierta" && !d.seller_responded_at;

  const respond = async (e) => {
    e.preventDefault();
    const paths = [];
    for (const f of files) paths.push(await upload(f, "private", "dispute"));
    const fd = new FormData();
    fd.append("photo_paths", paths.join(","));
    fd.append("response", response);
    await api.post(`/orders/${order.id}/dispute/respond`, fd, { headers: { "Content-Type": "multipart/form-data" } });
    toast.success("Respuesta enviada");
    reload();
  };
  return (
    <div className="bg-[#111] border border-[#EF4444]/30 rounded-md p-6">
      <h2 className="font-display text-xl mb-2 text-[#EF4444]">Disputa (Caso {d.case})</h2>
      <div className="text-sm text-neutral-300 mb-4"><b>Comprador:</b> {d.reason}</div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <div className="text-xs text-neutral-500 uppercase mb-2">Fotos del comprador</div>
          <div className="grid grid-cols-2 gap-2">
            {d.buyer_photos?.map((p) => <AuthImg key={p} path={p} width={300} className="w-full h-24 object-cover rounded border border-[#262626]" />)}
          </div>
        </div>
        <div>
          <div className="text-xs text-neutral-500 uppercase mb-2">Fotos del vendedor (publicación + respuesta)</div>
          <div className="grid grid-cols-2 gap-2">
            {order.publication_snapshot?.photos?.map((p) => <img key={p} src={publicImageUrl(p)} alt="" className="w-full h-24 object-cover rounded border border-[#262626]" />)}
            {d.seller_photos?.map((p) => <AuthImg key={p} path={p} width={300} className="w-full h-24 object-cover rounded border border-[#262626]" />)}
          </div>
        </div>
      </div>
      {d.seller_response && <div className="mt-4 text-sm text-neutral-300"><b>Respuesta vendedor:</b> {d.seller_response}</div>}
      {canRespond && (
        <form onSubmit={respond} className="mt-6 space-y-3 border-t border-[#262626] pt-4">
          <Textarea data-testid="dispute-seller-response" value={response} onChange={(e) => setResponse(e.target.value)} placeholder="Tu versión de los hechos" className="bg-[#171717] border-[#262626]" />
          <label className="flex items-center gap-2 text-sm text-neutral-400 cursor-pointer">
            <Upload size={16} /> {files.length ? `${files.length} foto(s)` : "Subir fotos adicionales"}
            <input data-testid="dispute-seller-photos" type="file" multiple accept="image/*" onChange={(e) => setFiles(Array.from(e.target.files || []))} className="hidden" />
          </label>
          <Button data-testid="dispute-seller-submit" type="submit" className="bg-[#EF4444] hover:bg-[#DC2626]">Enviar respuesta</Button>
        </form>
      )}
      {d.resolution && (
        <div className="mt-4 pt-4 border-t border-[#262626] text-sm">
          <div><b className="text-white">Resolución:</b> {d.resolution}</div>
          <div className="text-neutral-400 mt-1">{d.resolution_reason}</div>
        </div>
      )}
    </div>
  );
}

function RateForm({ orderId }) {
  const { user } = useAuth();
  const [stars, setStars] = useState(0);
  const [comment, setComment] = useState("");
  const [done, setDone] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [meta, setMeta] = useState(null); // { card_name, other_name, ... }

  useEffect(() => {
    if (!orderId) return;
    api.get("/ratings/pending").then((r) => {
      const m = r.data.find((p) => p.order_id === orderId);
      if (m) setMeta(m); else setDone(true);
    }).catch(() => {});
  }, [orderId, user?.id]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/ratings", { order_id: orderId, stars, comment });
      setDone(true);
      toast.success("Calificación enviada");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Ya calificaste");
    }
  };

  const skipPermanently = async () => {
    try {
      await api.post(`/orders/${orderId}/dismiss-rating`);
      setDismissed(true);
      toast.success("Omitido. No te lo recordaremos más para este pedido.");
    } catch { /* ignore */ }
  };

  if (done) return null;
  if (dismissed) return <div data-testid="rate-dismissed" className="bg-[#111] border border-[#262626] rounded-md p-4 text-neutral-500 text-sm">Omitiste la calificación de este pedido.</div>;
  if (hidden) return null;  // closed with X — reappears on reload/re-entry
  if (!meta) return null;   // still loading or already rated

  return (
    <form onSubmit={submit} className="bg-[#111] border border-[#262626] rounded-md p-6 space-y-4 relative" data-testid="rate-reminder">
      <button
        type="button"
        data-testid="rate-close-x"
        onClick={() => setHidden(true)}
        className="absolute top-3 right-3 text-neutral-500 hover:text-white"
        aria-label="Cerrar"
      >✕</button>
      <div>
        <h2 className="font-display text-xl">Califica tu {meta.role === "buyer" ? "compra" : "venta"}</h2>
        <p className="text-xs text-neutral-500 mt-1">Ayudas a la comunidad a saber con quién se está tratando.</p>
      </div>
      <div className="flex items-center gap-3 p-3 bg-[#0A0A0A] border border-[#262626] rounded" data-testid="rate-item-context">
        {meta.card_image_url && <img src={cardImageUrl(meta.card_image_url)} alt="" className="w-10 h-14 object-contain rounded" onError={(e) => e.currentTarget.style.display = "none"} />}
        <div className="text-sm">
          <div className="text-white">{meta.card_name || "—"} <span className="text-neutral-500 text-xs">x{meta.quantity}</span></div>
          <div className="text-neutral-500 text-xs">{(meta.language || "").toUpperCase()}{meta.variant ? ` · ${meta.variant}` : ""}</div>
          <div className="text-neutral-400 text-xs mt-1">{meta.role === "buyer" ? "Vendedor" : "Comprador"}: <b className="text-white">{meta.other_name || "—"}</b></div>
        </div>
      </div>
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} type="button" onClick={() => setStars(n)} data-testid={`rate-star-${n}`}>
            <Star size={24} className={n <= stars ? "fill-[#EF4444] text-[#EF4444]" : "text-neutral-600"} />
          </button>
        ))}
      </div>
      <Textarea data-testid="rate-comment" value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Comentario opcional" className="bg-[#171717] border-[#262626]" />
      <div className="flex flex-wrap gap-2">
        <Button data-testid="rate-submit" type="submit" className="bg-[#EF4444] hover:bg-[#DC2626]">Enviar calificación</Button>
        <Button
          type="button"
          data-testid="rate-skip"
          variant="ghost"
          onClick={skipPermanently}
          className="text-neutral-400 hover:text-white"
        >Omitir / Ahora no</Button>
      </div>
    </form>
  );
}

function AdminActions({ order, reload }) {
  const [resolution, setResolution] = useState("buyer_full");
  const [reason, setReason] = useState("");
  const [partialPct, setPartial] = useState(50);
  const [customBuyerRefund, setCustomBuyerRefund] = useState(0);
  const [customSellerPayout, setCustomSellerPayout] = useState(0);
  const [strikeS, setStrikeS] = useState(false);
  const [strikeB, setStrikeB] = useState(false);
  const [releaseOpen, setReleaseOpen] = useState(false);
  const [payoutInfo, setPayoutInfo] = useState(null);
  const [receiptNumber, setReceiptNumber] = useState("");
  const [receiptDate, setReceiptDate] = useState(new Date().toISOString().slice(0, 10));
  const [releaseLoading, setReleaseLoading] = useState(false);
  const [refundBusy, setRefundBusy] = useState(false);
  const { prompt, confirm } = useDialog();
  const errMsg = (err) => err?.response?.data?.detail || "No se pudo completar la acción";
  const total = `$${Number(order.total || 0).toFixed(2)}`;

  const openRelease = async () => {
    try {
      const r = await api.get(`/admin/orders/${order.id}/payout-info`);
      setPayoutInfo(r.data);
    } catch { setPayoutInfo(null); }
    setReleaseOpen(true);
  };

  const doRelease = async (e) => {
    e.preventDefault();
    if (!receiptNumber.trim() || !receiptDate) { toast.error("Comprobante y fecha requeridos"); return; }
    const net = payoutInfo?.net_amount != null ? `$${payoutInfo.net_amount.toFixed(2)}` : "el neto fijado del pedido";
    if (!(await confirm({ title: "¿Liberar el pago?", description: `Esto libera ${net} al vendedor y no se puede deshacer.`, confirmText: "Sí, liberar", destructive: true }))) return;
    setReleaseLoading(true);
    try {
      await api.post(`/admin/orders/${order.id}/release`, { receipt_number: receiptNumber, receipt_date: receiptDate });
      toast.success("Pago liberado. Vendedor notificado por email.");
      setReleaseOpen(false); setReceiptNumber(""); reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally { setReleaseLoading(false); }
  };
  const doRefund = async () => {
    if (refundBusy) return;
    const r = await prompt({ title: "Reembolsar al comprador", description: `Esto inicia el reembolso de ${total} al comprador y no se puede deshacer.`, label: "Motivo (queda en el log de auditoría)", multiline: true, required: true });
    if (!r) return;
    if (!(await confirm({ title: "¿Confirmas el reembolso?", description: `Reembolso de ${total} al comprador. No se puede deshacer.`, confirmText: "Sí, reembolsar", destructive: true }))) return;
    setRefundBusy(true);
    try {
      const res = await api.post(`/admin/orders/${order.id}/refund?reason=${encodeURIComponent(r)}`);
      if (res.data?.method === "manual") {
        toast.warning("Reembolso NO completado: pendiente de transferencia manual.", { duration: 10000 });
      } else {
        toast.success(res.data?.message || "Reembolsado");
      }
      reload();
    } catch (err) { toast.error(errMsg(err)); } finally { setRefundBusy(false); }
  };
  const doRestoreStock = async () => {
    const r = await prompt({ title: "Restaurar stock", description: "Usa esto solo si el vendedor SÍ tiene las cartas. Vuelven a estar visibles y a la venta. No mueve dinero.", label: "Motivo (queda en el log de auditoría)", multiline: true, required: true });
    if (!r) return;
    try {
      const res = await api.post(`/admin/orders/${order.id}/restore-stock`, { reason: r });
      toast.success(`Stock restaurado (${res.data.restored.length} publicaciones)`); reload();
    } catch (err) { toast.error(errMsg(err)); }
  };
  const doCancel = async () => {
    const unpaid = ["PENDIENTE_PAGO", "RECHAZADO"].includes(order.status);
    const consequence = unpaid
      ? "Cancela el pedido sin cobro y devuelve la carta al catálogo. No mueve dinero."
      : `Cancela el pedido, registra un reembolso de ${total} al comprador y devuelve la carta al catálogo. No se puede deshacer.`;
    const r = await prompt({ title: "Cancelar pedido (emergencia)", description: consequence, label: "Motivo (queda en el log de auditoría)", multiline: true, required: true });
    if (!r) return;
    if (!(await confirm({ title: "¿Cancelar el pedido?", description: consequence, confirmText: "Sí, cancelar", destructive: true }))) return;
    try {
      await api.post(`/admin/orders/${order.id}/cancel`, { reason: r });
      toast.success("Pedido cancelado"); reload();
    } catch (err) { toast.error(errMsg(err)); }
  };
  const doForceDeliver = async () => {
    // Doble confirmación por texto libre — pedimos que el admin escriba "SI"
    // para evitar clicks accidentales. No liberar pago, solo cerrar entrega.
    const ok = await prompt({
      title: "Marcar entregado (forzar)",
      label: 'Vas a cerrar la entrega en nombre del comprador. NO libera el pago al vendedor — eso sigue haciéndose manual desde /admin/payouts. Escribe "SI" para confirmar.',
      required: true,
    });
    if (!ok || String(ok).trim().toUpperCase() !== "SI") { toast.error("Cancelado — debes escribir SI para confirmar"); return; }
    const why = await prompt({ title: "Motivo", label: "Motivo (queda en el log de auditoría)", multiline: true, required: true });
    if (!why) return;
    try {
      await api.post(`/admin/orders/${order.id}/mark-delivered`, { reason: why });
      toast.success("Entrega cerrada. Comprador y vendedor notificados por email.");
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al marcar como entregada");
    }
  };
  const resolveDispute = async (e) => {
    e.preventDefault();
    if (resolution === "other" && !reason.trim()) { toast.error("Detalla la resolución"); return; }
    const buyerR = parseFloat(customBuyerRefund) || 0;
    const sellerP = parseFloat(customSellerPayout) || 0;
    if (resolution === "other" && (buyerR + sellerP) > order.total + 0.01) {
      toast.error(`La suma ($${(buyerR + sellerP).toFixed(2)}) excede el total pagado ($${order.total.toFixed(2)})`);
      return;
    }
    const moneyText = {
      buyer_full: `Reembolsa ${total} al comprador.`,
      buyer_partial: `Reembolsa el ${partialPct}% del precio al comprador y libera el resto al vendedor.`,
      transit: "Libera el neto al vendedor. El comprador NO recibe reembolso.",
      seller: "Libera el neto al vendedor. El comprador NO recibe reembolso.",
      other: `Reembolsa $${buyerR.toFixed(2)} al comprador y paga $${sellerP.toFixed(2)} al vendedor.`,
    }[resolution];
    if (!(await confirm({ title: "¿Resolver la disputa?", description: `${moneyText} No se puede deshacer.`, confirmText: "Sí, resolver", destructive: true }))) return;
    try {
      await api.post(`/admin/orders/${order.id}/dispute/resolve`, {
        resolution, reason,
        partial_pct: parseFloat(partialPct),
        buyer_refund_amount: resolution === "other" ? buyerR : undefined,
        seller_payout_amount: resolution === "other" ? sellerP : undefined,
        strike_seller: strikeS, strike_buyer: strikeB,
      });
      toast.success("Disputa resuelta"); reload();
    } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <div className="bg-[#111] border border-[#EF4444]/30 rounded-md p-6">
      <h2 className="font-display text-xl mb-4 text-[#EF4444]">Acciones de admin</h2>
      <AdminOrderControls order={order} reload={reload} />
      {(order.status === "ENTREGADO" || order.status === "PENDIENTE_LIBERACION") && (
        <Button data-testid="admin-release" onClick={openRelease} className="bg-emerald-600 hover:bg-emerald-700 mr-2">Liberar pago al vendedor</Button>
      )}
      {order.status === "ENVIADO" && (
        /* Forzar cierre de entrega. Solo cuando la orden está ENVIADO — es el
           único estado del que tiene sentido "adelantar" al confirm-received.
           No libera pago: la orden entra a la cola /admin/payouts como si
           el comprador hubiera confirmado normalmente. */
        <Button
          data-testid="admin-force-deliver"
          onClick={doForceDeliver}
          className="bg-amber-600 hover:bg-amber-700 mr-2"
          title="Cierra la entrega en nombre del comprador. NO libera el pago."
        >
          Marcar entregado (forzar)
        </Button>
      )}
      {["CANCELADO", "REEMBOLSADO", "REEMBOLSO_PENDIENTE"].includes(order.status) && order.paid_at && !order.stock_restored_at && (
        <Button data-testid="admin-restore-stock" onClick={doRestoreStock} variant="outline" className="border-emerald-600 text-emerald-400 mr-2">Restaurar stock</Button>
      )}
      {order.stock_restored_at && (
        <div className="text-[11px] text-emerald-400 mt-1" data-testid="admin-stock-restored">Stock restaurado</div>
      )}
      {["PAGADO", "ENVIADO", "ENTREGADO", "PENDIENTE_LIBERACION"].includes(order.status) && (
        <Button data-testid="admin-refund" onClick={doRefund} disabled={refundBusy} variant="outline" className="border-[#EF4444] text-[#EF4444] mr-2">Reembolsar</Button>
      )}
      {!["LIBERADO", "REEMBOLSADO", "CANCELADO", "REEMBOLSO_PENDIENTE"].includes(order.status) && (
        <Button data-testid="admin-cancel" onClick={doCancel} variant="outline" className="border-amber-500 text-amber-400">Cancelar pedido</Button>
      )}
      {order.status === "EN_DISPUTA" && !order.dispute?.resolution && (
        <form onSubmit={resolveDispute} className="space-y-3 mt-4">
          <Select value={resolution} onValueChange={setResolution}>
            <SelectTrigger data-testid="admin-resolution" className="bg-[#171717] border-[#262626]"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#171717] border-[#262626] text-white">
              <SelectItem value="buyer_full">Reclamo válido → devolución total</SelectItem>
              <SelectItem value="buyer_partial">Reclamo parcial → devolución parcial</SelectItem>
              <SelectItem value="transit">Daño en tránsito (courier) → vendedor cobra, comprador NO se reembolsa</SelectItem>
              <SelectItem value="seller">Reclamo inválido → paga el vendedor</SelectItem>
              <SelectItem value="other">Otro → resolución personalizada por el admin</SelectItem>
            </SelectContent>
          </Select>
          {resolution === "buyer_partial" && (
            <div>
              <Label className="text-xs text-neutral-400">% de devolución</Label>
              <Input data-testid="admin-partial-pct" type="number" min="1" max="100" value={partialPct} onChange={(e) => setPartial(e.target.value)} className="bg-[#171717] border-[#262626]" />
            </div>
          )}
          {resolution === "other" && (
            <div className="space-y-3 p-3 bg-amber-500/5 border border-amber-500/30 rounded-md" data-testid="admin-other-fields">
              <div className="text-xs text-amber-200">Escribe la resolución personalizada. El texto que ingreses aquí es EL QUE SE ENVÍA por correo al comprador y al vendedor.</div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-neutral-400">Reembolso al comprador ($)</Label>
                  <Input data-testid="admin-custom-refund" type="number" step="0.01" min="0" max={order.total} value={customBuyerRefund} onChange={(e) => setCustomBuyerRefund(e.target.value)} className="bg-[#171717] border-[#262626]" />
                </div>
                <div>
                  <Label className="text-xs text-neutral-400">Pago al vendedor ($)</Label>
                  <Input data-testid="admin-custom-payout" type="number" step="0.01" min="0" max={order.total} value={customSellerPayout} onChange={(e) => setCustomSellerPayout(e.target.value)} className="bg-[#171717] border-[#262626]" />
                </div>
              </div>
              <div className="text-[10px] text-neutral-500">Total pagado por el comprador: <b className="text-white">${order.total.toFixed(2)}</b>. Suma actual: <b className={((parseFloat(customBuyerRefund) || 0) + (parseFloat(customSellerPayout) || 0)) > order.total ? "text-red-400" : "text-emerald-400"}>${((parseFloat(customBuyerRefund) || 0) + (parseFloat(customSellerPayout) || 0)).toFixed(2)}</b></div>
            </div>
          )}
          <Textarea data-testid="admin-reason" value={reason} onChange={(e) => setReason(e.target.value)} required placeholder="Motivo de la resolución (queda en el log)" className="bg-[#171717] border-[#262626]" />
          <div className="flex gap-4 text-sm">
            <label className="flex items-center gap-2"><input data-testid="admin-strike-seller" type="checkbox" checked={strikeS} onChange={(e) => setStrikeS(e.target.checked)} /> Strike al vendedor</label>
            <label className="flex items-center gap-2"><input data-testid="admin-strike-buyer" type="checkbox" checked={strikeB} onChange={(e) => setStrikeB(e.target.checked)} /> Strike al comprador</label>
          </div>
          <Button data-testid="admin-resolve-submit" type="submit" className="bg-[#EF4444] hover:bg-[#DC2626]">Resolver disputa</Button>
        </form>
      )}

      {releaseOpen && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 pt-[max(1rem,env(safe-area-inset-top))] pb-[max(1rem,env(safe-area-inset-bottom))]" onClick={() => setReleaseOpen(false)}>
          <form onSubmit={doRelease} className="bg-[#111] border border-[#262626] rounded-md p-6 max-w-lg w-full max-h-[calc(100dvh-4rem)] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="release-modal">
            <div className="flex justify-between items-start mb-4">
              <h3 className="font-display text-2xl">Liberar pago al vendedor</h3>
              <button type="button" onClick={() => setReleaseOpen(false)} className="text-neutral-500 hover:text-white"><XCircle size={20} /></button>
            </div>
            {payoutInfo?.seller?.payout_info ? (
              <div className="mb-4 p-4 bg-[#0A0A0A] border border-[#262626] rounded-md text-sm space-y-1" data-testid="release-payout-info">
                <div className="text-xs text-neutral-500 uppercase tracking-wider mb-2">Datos de cobro del vendedor</div>
                <div><span className="text-neutral-500">Titular:</span> <b className="text-white">{payoutInfo.seller.payout_info.holder_name}</b></div>
                <div><span className="text-neutral-500">Cédula:</span> <span className="font-mono text-white">{payoutInfo.seller.payout_info.holder_cedula}</span></div>
                <div><span className="text-neutral-500">Banco:</span> <b className="text-white">{payoutInfo.seller.payout_info.bank_name}</b></div>
                <div><span className="text-neutral-500">Tipo:</span> <b className="text-white capitalize">{payoutInfo.seller.payout_info.account_type}</b></div>
                <div><span className="text-neutral-500">N° cuenta:</span> <span className="font-mono text-white select-all">{payoutInfo.seller.payout_info.account_number}</span></div>
                <div className="pt-2 border-t border-[#262626] mt-2 flex justify-between items-center">
                  <span className="text-neutral-500">Monto neto a transferir</span>
                  <b className="text-emerald-400 font-display text-xl" data-testid="release-net-amount">${payoutInfo.net_amount?.toFixed(2)}</b>
                </div>
              </div>
            ) : (
              <div className="mb-4 p-4 bg-amber-500/10 border border-amber-500/30 rounded-md text-sm text-amber-300" data-testid="release-no-payout">
                ⚠ Este vendedor no tiene datos de cobro registrados. Contáctalo antes de liberar el pago.
              </div>
            )}
            <div className="space-y-3">
              <div>
                <Label className="text-xs text-neutral-400 mb-2 block">N° de comprobante de transferencia</Label>
                <Input data-testid="release-receipt-number" value={receiptNumber} onChange={(e) => setReceiptNumber(e.target.value)} placeholder="Ej: TRF-20260215-001" required className="bg-[#171717] border-[#262626] font-mono" />
              </div>
              <div>
                <Label className="text-xs text-neutral-400 mb-2 block">Fecha de la transferencia</Label>
                <Input data-testid="release-receipt-date" type="date" value={receiptDate} onChange={(e) => setReceiptDate(e.target.value)} required className="bg-[#171717] border-[#262626]" />
              </div>
            </div>
            <div className="flex gap-2 mt-6">
              <Button data-testid="release-confirm" type="submit" disabled={releaseLoading} className="bg-emerald-600 hover:bg-emerald-700">
                <BusyLabel busy={releaseLoading} text="Liberando…">Confirmar liberación</BusyLabel>
              </Button>
              <Button type="button" variant="ghost" onClick={() => setReleaseOpen(false)}>Cancelar</Button>
            </div>
          </form>
        </div>
      )}

      {order.release_receipt && (
        <div className="mt-4 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded text-sm" data-testid="release-receipt-info">
          <div className="text-emerald-300 font-semibold mb-1">Pago liberado</div>
          <div className="text-neutral-300">N° comprobante: <span className="font-mono">{order.release_receipt.number}</span></div>
          <div className="text-neutral-400 text-xs">Fecha transferencia: {order.release_receipt.date}</div>
        </div>
      )}
    </div>
  );
}

function ShipTimer({ order, role = "buyer", onRefund }) {
  const [now, setNow] = useState(Date.now());
  const { prompt } = useDialog();
  const isMeetup = order.delivery_method === "meetup";
  const isBuyer = role === "buyer";
  useEffect(() => {
    const iv = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(iv);
  }, []);
  const deadline = order.ship_deadline_at ? new Date(order.ship_deadline_at).getTime() : null;
  const diff = deadline ? deadline - now : null;
  const past = diff !== null && diff <= 0;
  const parts = (() => {
    if (diff === null || diff <= 0) return null;
    const s = Math.floor(diff / 1000);
    const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
    return { d, h, m, s: sec };
  })();
  const requestRefund = async () => {
    const reason = await prompt({
      title: past ? "Solicitar reembolso automático" : "Solicitar reembolso",
      label: past ? "Confirma el motivo (auto-aprobado por vencimiento)" : "Motivo — un admin revisará tu solicitud",
      multiline: true, required: true,
    });
    if (!reason) return;
    const r = await api.post(`/orders/${order.id}/buyer-refund-request`, { reason, details: "", photo_paths: [] });
    if (r.data.auto_approved) toast.success("Reembolso procesado automáticamente");
    else toast.success("Solicitud enviada al admin");
    onRefund();
  };
  const countdown = parts ? `${parts.d}d ${String(parts.h).padStart(2,"0")}h ${String(parts.m).padStart(2,"0")}m ${String(parts.s).padStart(2,"0")}s` : "—";
  const activeLabel = isMeetup
    ? (isBuyer ? `El vendedor tiene ${countdown} para coordinar el encuentro` : `Tienes ${countdown} para concretar el encuentro`)
    : (isBuyer ? `El vendedor tiene ${countdown} para despachar` : `Tienes ${countdown} para despachar la carta`);
  const pastLabel = isMeetup ? "No se concretó el encuentro en el plazo" : (isBuyer ? "El vendedor no envió en el plazo" : "Venciste el plazo de envío");
  const activeInfo = isBuyer
    ? "Al vencer el plazo aparecerá el botón de reembolso automático aquí. Si el vendedor sube la guía antes, el timer desaparece."
    : "Sube la guía antes de que llegue a 0. Si vence, el comprador puede pedir reembolso automático y recibirás un strike.";
  const pastInfo = isBuyer
    ? "Puedes pedir el reembolso automático ahora. Se te devuelve tu pago y el vendedor recibe un strike."
    : "El comprador ya puede solicitar el reembolso automático. Un strike puede aplicarse a tu cuenta.";
  return (
    <div className={`bg-[#111] border rounded-md p-4 ${past ? "border-[#EF4444]" : "border-amber-500/40"}`} data-testid={`ship-timer-${role}`}>
      <div className="flex items-center gap-2 mb-2">
        <Clock size={16} className={past ? "text-[#EF4444]" : "text-amber-400"} />
        <span className={`font-medium ${past ? "text-[#EF4444]" : "text-amber-300"}`}>
          {past ? pastLabel : activeLabel}
        </span>
      </div>
      <p className="text-sm text-neutral-400 mb-3">
        {past ? pastInfo : activeInfo}
      </p>
      {past && isBuyer && (
        <Button data-testid="buyer-refund-btn" onClick={requestRefund} className="bg-[#EF4444] hover:bg-[#DC2626]">
          <XCircle size={14} className="mr-1" /> Reembolso automático
        </Button>
      )}
    </div>
  );
}


function ReleaseTimer({ order, role = "buyer", onReload }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const iv = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(iv);
  }, []);
  const target = order.release_target_at ? new Date(order.release_target_at).getTime() : null;
  const diff = target ? target - now : null;
  const past = diff !== null && diff <= 0;
  const parts = (() => {
    if (diff === null || diff <= 0) return null;
    const s = Math.floor(diff / 1000);
    const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
    return { d, h, m, s: sec };
  })();
  const countdown = parts ? `${parts.d}d ${String(parts.h).padStart(2,"0")}h ${String(parts.m).padStart(2,"0")}m ${String(parts.s).padStart(2,"0")}s` : "—";
  const isBuyer = role === "buyer";
  const activeLabel = isBuyer
    ? `Ventana de cierre: te quedan ${countdown} para confirmar o abrir disputa`
    : `Se libera tu pago en ${countdown}`;
  const activeInfo = isBuyer
    ? "Si no confirmas recepción ni abres disputa antes de que llegue a 0, el pedido se cerrará automáticamente y el pago se liberará al vendedor. La opción de abrir disputa está disponible hasta el momento del cierre."
    : "Si el comprador no confirma ni abre disputa dentro de este plazo, el pedido se cierra solo y HitTCG procesará tu desembolso.";
  const closingSoon = diff !== null && diff > 0 && diff < 6 * 3600 * 1000;
  const borderCls = past ? "border-emerald-600/50" : closingSoon ? "border-amber-500/40" : "border-[#262626]";
  return (
    <div className={`bg-[#111] border rounded-md p-4 ${borderCls}`} data-testid={`release-timer-${role}`}>
      <div className="flex items-center gap-2 mb-2">
        <Clock size={16} className={past ? "text-emerald-400" : closingSoon ? "text-amber-400" : "text-neutral-400"} />
        <span className={`font-medium ${past ? "text-emerald-300" : closingSoon ? "text-amber-300" : "text-white"}`}>
          {past ? (isBuyer ? "Ventana cerrada — pedido liberado" : "Pedido liberado — pago desembolsado") : activeLabel}
        </span>
      </div>
      <p className="text-sm text-neutral-400">{past ? "Recarga la página para ver el estado final." : activeInfo}</p>
    </div>
  );
}


function RefundCompletedBanner({ order, isBuyer }) {
  const method = order.refund_method === "auto" ? "Reverso automático a la tarjeta" : "Transferencia bancaria manual";
  return (
    <div className="bg-[#111] border border-emerald-500/40 rounded-md p-5" data-testid="refund-completed">
      <div className="flex items-center gap-2 mb-2">
        <div className="text-emerald-400 text-lg">✓</div>
        <div className="font-display text-lg text-emerald-200">Reembolso completado</div>
      </div>
      <div className="text-sm text-neutral-300">
        {isBuyer
          ? `Ya te devolvimos $${Number(order.total).toFixed(2)}.`
          : `El comprador recibió $${Number(order.total).toFixed(2)} de vuelta.`}{" "}
        <b className="text-white">Método:</b> {method}.
        {order.refund_reason && (
          <div className="text-xs text-neutral-500 mt-1">Motivo: {order.refund_reason}</div>
        )}
      </div>
    </div>
  );
}

function RefundPendingBlock({ order, isBuyer, reload }) {
  const stage = order.refund_status || "awaiting_bank_info";
  const [form, setForm] = useState({
    bank: "", account_type: "ahorros", account_number: "", holder_name: "", holder_cedula: "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.bank.trim() || !form.account_number.trim() || !form.holder_name.trim() || !form.holder_cedula.trim()) {
      toast.error("Completa todos los campos");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/orders/${order.id}/refund-bank-info`, form);
      toast.success("Datos enviados. El admin realizará la transferencia y verás el comprobante aquí.", { duration: 6000 });
      reload && reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo enviar");
    } finally {
      setBusy(false);
    }
  };

  if (stage === "awaiting_bank_info") {
    if (!isBuyer) {
      return (
        <div className="bg-[#111] border border-amber-500/40 rounded-md p-5" data-testid="refund-awaiting-bank-seller">
          <div className="font-display text-lg text-amber-200 mb-1">Reembolso pendiente al comprador</div>
          <div className="text-sm text-neutral-300">
            No fue posible el reverso automático de Payphone. El comprador está cargando sus datos bancarios para que HitTCG haga la transferencia manual.
          </div>
        </div>
      );
    }
    return (
      <div className="bg-[#111] border border-amber-500/40 rounded-md p-6 space-y-4" data-testid="refund-bank-info-form">
        <div>
          <div className="font-display text-xl text-amber-200 mb-1">Cargar datos para tu devolución</div>
          <div className="text-xs text-neutral-400 leading-relaxed">
            El reverso automático a tu tarjeta no fue posible (ya pasó la ventana del mismo día antes de 20:00). Cárganos tus datos bancarios y te haremos la <b>transferencia manual por ${Number(order.total).toFixed(2)}</b> lo antes posible.
          </div>
        </div>
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="text-neutral-400 text-xs uppercase tracking-wider">Banco</Label>
            <BankSelect
              testId="refund-bank"
              value={form.bank}
              onChange={(v) => setForm({ ...form, bank: v })}
            />
          </div>
          <div>
            <Label className="text-neutral-400 text-xs uppercase tracking-wider">Tipo de cuenta</Label>
            <select data-testid="refund-account-type" value={form.account_type} onChange={(e) => setForm({ ...form, account_type: e.target.value })} className="w-full bg-[#171717] border border-[#262626] rounded-md h-10 px-3 text-white">
              <option value="ahorros">Ahorros</option>
              <option value="corriente">Corriente</option>
            </select>
          </div>
          <div>
            <Label className="text-neutral-400 text-xs uppercase tracking-wider">Número de cuenta</Label>
            <Input data-testid="refund-account-number" value={form.account_number} onChange={(e) => setForm({ ...form, account_number: e.target.value.replace(/\D/g, "") })} placeholder="20 dígitos máx." className="bg-[#171717] border-[#262626]" />
          </div>
          <div>
            <Label className="text-neutral-400 text-xs uppercase tracking-wider">Cédula del titular</Label>
            <Input data-testid="refund-holder-cedula" value={form.holder_cedula} onChange={(e) => setForm({ ...form, holder_cedula: e.target.value.replace(/\D/g, "").slice(0, 10) })} placeholder="10 dígitos" className="bg-[#171717] border-[#262626]" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-neutral-400 text-xs uppercase tracking-wider">Nombre completo del titular</Label>
            <Input data-testid="refund-holder-name" value={form.holder_name} onChange={(e) => setForm({ ...form, holder_name: e.target.value })} placeholder="Como figura en el banco" className="bg-[#171717] border-[#262626]" />
          </div>
          <div className="md:col-span-2 flex justify-end">
            <Button type="submit" disabled={busy} className="bg-[#EF4444] hover:bg-[#DC2626]" data-testid="refund-bank-submit">
              <BusyLabel busy={busy} text="Enviando…">Enviar datos</BusyLabel>
            </Button>
          </div>
        </form>
      </div>
    );
  }

  if (stage === "awaiting_transfer") {
    const bi = order.refund_bank_info || {};
    return (
      <div className="bg-[#111] border border-amber-500/40 rounded-md p-5" data-testid="refund-awaiting-transfer">
        <div className="font-display text-lg text-amber-200 mb-1">Transferencia en proceso</div>
        <div className="text-sm text-neutral-300 mb-3">
          {isBuyer
            ? `Recibimos tus datos. HitTCG te transferirá $${Number(order.total).toFixed(2)} en las próximas horas hábiles.`
            : "El comprador cargó sus datos bancarios. El admin realizará la transferencia."}
        </div>
        {bi.bank && (
          <div className="text-xs text-neutral-500 bg-black/40 border border-[#262626] rounded p-3">
            <div>Banco: <b className="text-white">{bi.bank}</b> · {bi.account_type}</div>
            <div>Cuenta: <b className="text-white">{bi.account_number}</b></div>
            <div>Titular: <b className="text-white">{bi.holder_name}</b> · Cédula {bi.holder_cedula}</div>
          </div>
        )}
      </div>
    );
  }

  return null;
}


function ShipExtensionBlock({ order, isBuyer, isSeller, reload }) {
  const { confirm } = useDialog();
  const ext = order.ship_extension_request;
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const requestExt = async (e) => {
    e.preventDefault();
    if (reason.trim().length < 10) { toast.error("Explica brevemente por qué (mín 10 caracteres)"); return; }
    setBusy(true);
    try {
      await api.post(`/orders/${order.id}/request-extension`, { reason: reason.trim() });
      toast.success("Solicitud enviada al comprador");
      setReason("");
      reload && reload();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); } finally { setBusy(false); }
  };

  const decide = async (approve) => {
    const ok = await confirm({
      title: approve ? "Aprobar 24h extra" : "Rechazar 24h extra",
      description: approve
        ? "El vendedor tendrá 24h más para enviar tu pedido antes de que se dispare el auto-reembolso. ¿Confirmar?"
        : "El plazo original sigue vigente. Si el vendedor no envía a tiempo, podrás pedir el reembolso automático.",
      confirmLabel: approve ? "Sí, aprobar" : "Sí, rechazar",
      danger: !approve,
    });
    if (!ok) return;
    setBusy(true);
    try {
      await api.post(`/orders/${order.id}/decide-extension`, { approve });
      toast.success(approve ? "Extensión aprobada (+24h)" : "Solicitud rechazada");
      reload && reload();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); } finally { setBusy(false); }
  };

  // No prior request → seller sees form, buyer sees nothing.
  if (!ext) {
    if (!isSeller) return null;
    return (
      <div className="bg-[#111] border border-amber-500/30 rounded-md p-4" data-testid="ship-extension-request-form">
        <div className="flex items-start gap-2 mb-2">
          <Clock size={16} className="text-amber-400 mt-0.5" />
          <div className="text-sm">
            <div className="font-medium text-amber-200">¿Necesitas 24h más para enviar?</div>
            <div className="text-[11px] text-neutral-400 mt-0.5">Si el courier se demoró o hay un imprevisto, puedes pedir un extra al comprador antes de que salte el auto-reembolso. El comprador aprueba o rechaza.</div>
          </div>
        </div>
        <form onSubmit={requestExt} className="space-y-2 mt-2">
          <Textarea
            data-testid="ship-extension-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Motivo (mín 10 caracteres). Ej: courier se retrasó por feriado…"
            className="bg-[#171717] border-[#262626] text-white text-sm"
            rows={2}
          />
          <Button type="submit" size="sm" disabled={busy} className="bg-amber-600 hover:bg-amber-700" data-testid="ship-extension-submit">
            <BusyLabel busy={busy} text="Enviando…">Pedir 24h extra</BusyLabel>
          </Button>
        </form>
      </div>
    );
  }

  const bg = ext.status === "pending" ? "border-amber-500/50" : ext.status === "approved" ? "border-emerald-500/40" : "border-red-500/40";
  const label = ext.status === "pending" ? "Extensión pendiente de decisión" : ext.status === "approved" ? "Extensión aprobada (+24h)" : "Extensión rechazada";
  const color = ext.status === "pending" ? "text-amber-200" : ext.status === "approved" ? "text-emerald-200" : "text-red-200";

  return (
    <div className={`bg-[#111] border rounded-md p-4 ${bg}`} data-testid={`ship-extension-status-${ext.status}`}>
      <div className={`text-sm font-medium ${color} mb-1`}>{label}</div>
      <div className="text-xs text-neutral-400"><b>Motivo del vendedor:</b> {ext.reason}</div>
      {ext.status === "pending" && isBuyer && (
        <div className="flex gap-2 mt-3">
          <Button size="sm" onClick={() => decide(true)} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="ship-extension-approve">
            Aprobar +24h
          </Button>
          <Button size="sm" variant="ghost" onClick={() => decide(false)} disabled={busy} className="text-red-400 hover:bg-red-500/10" data-testid="ship-extension-reject">
            Rechazar
          </Button>
        </div>
      )}
      {ext.status === "pending" && isSeller && (
        <div className="text-[11px] text-neutral-500 mt-2">Esperando decisión del comprador. Se te avisará cuando responda.</div>
      )}
    </div>
  );
}



// ============================================================
// TRANSFERENCIA BANCARIA — form + banners
// ============================================================
function TransferReceiptForm({ order, reload }) {
  const { confirm } = useDialog();
  const [bank, setBank] = useState(null);
  const [interbankFee, setInterbankFee] = useState(0.40);
  const [file, setFile] = useState(null);
  const [form, setForm] = useState({
    receipt_number: "",
    bank_from: "",
    amount_reported: String(order.total?.toFixed(2) ?? ""),
  });
  const [busy, setBusy] = useState(false);
  const deadline = order.payment_deadline_at ? new Date(order.payment_deadline_at) : null;

  useEffect(() => {
    api.get("/config/public").then((r) => {
      setBank(r.data?.hittcg_bank_info || null);
      setInterbankFee(Number(r.data?.interbank_transfer_fee ?? 0.40));
    }).catch(() => setBank(null));
  }, []);

  const isPichincha = (form.bank_from || "").toLowerCase().includes("pichincha");
  const hittcgIsPichincha = ((bank?.bank_name) || "").toLowerCase().includes("pichincha");
  const showInterbankWarn = hittcgIsPichincha && form.bank_from.trim().length > 0 && !isPichincha;

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Sube la foto o PDF del comprobante"); return; }
    if (!form.receipt_number.trim()) { toast.error("Ingresa el N° de comprobante"); return; }
    if (!form.bank_from.trim()) { toast.error("Indica desde qué banco transferiste"); return; }
    const amount = Number(form.amount_reported);
    if (!(amount > 0)) { toast.error("Monto inválido"); return; }
    if (Math.abs(amount - Number(order.total || 0)) > 0.01) {
      const ok = await confirm({
        title: "Monto no coincide con el pedido",
        description: `El total del pedido es $${Number(order.total).toFixed(2)}. Tu transferencia dice $${amount.toFixed(2)}. Si es correcto, continúa — el admin verificará antes de confirmar. ¿Enviar así?`,
        confirmLabel: "Enviar de todos modos",
        danger: true,
      });
      if (!ok) return;
    }
    setBusy(true);
    try {
      const receipt_path = await upload(file, "private", "transfer_receipt");
      await api.post(`/orders/${order.id}/transfer/submit-receipt`, {
        receipt_path,
        receipt_number: form.receipt_number.trim(),
        bank_from: form.bank_from.trim(),
        amount_reported: amount,
      });
      toast.success("Tu pago está en revisión. La carta queda reservada para ti; te avisamos cuando se apruebe.");
      reload && reload();
    } catch (err) {
      // Feb 2026 — Candado A: comprobante único. Backend devuelve 409
      // con {code:"duplicate_receipt_number", existing_order_id, message}.
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && detail?.code === "duplicate_receipt_number") {
        toast.error(detail.message || "Este comprobante ya está registrado en otro pedido", { duration: 9000 });
      } else {
        toast.error((typeof detail === "string" ? detail : detail?.message) || "No se pudo enviar el comprobante");
      }
    } finally {
      setBusy(false);
    }
  };

  const rejected = order.transfer_verification?.status === "rejected";
  const bankReady = bank && (bank.bank_name || "").trim() && (bank.account_number || "").trim();

  return (
    <div className="space-y-4" data-testid="transfer-receipt-form">
      {rejected && (
        <div className="bg-red-500/10 border border-red-500/40 rounded-md p-4 text-sm" data-testid="transfer-rejected-banner">
          <div className="flex items-center gap-2 text-red-300 font-semibold mb-1">
            <XCircle size={16} />Comprobante rechazado por el admin
          </div>
          <div className="text-red-200/90"><b>Motivo:</b> {order.transfer_verification.reject_reason}</div>
          <div className="text-neutral-400 text-xs mt-2">Sube un comprobante corregido o retoma el pago.</div>
        </div>
      )}

      <div className="bg-[#111] border border-[#EF4444]/40 rounded-md p-6 space-y-4">
        <div className="flex items-center gap-2">
          <Landmark size={20} className="text-[#EF4444]" />
          <h2 className="font-display text-xl">Transfiere a la cuenta de HitTCG</h2>
        </div>
        <div className="text-sm text-neutral-400 leading-relaxed">
          Tu pago va a la <b className="text-white">cuenta de HitTCG</b>, no directo al vendedor. El dinero queda <b className="text-white">retenido</b> hasta que confirmes recepción — misma protección que con tarjeta.
        </div>
        {deadline && (
          <div className="text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded p-2" data-testid="transfer-deadline">
            <Clock size={12} className="inline mr-1" />Tiempo límite para subir el comprobante: <b>{deadline.toLocaleString("es-EC")}</b>. Si no lo subes, el pedido se cancela y las cartas vuelven al catálogo.
          </div>
        )}
        {!bankReady ? (
          <div className="text-sm text-red-300 bg-red-500/10 border border-red-500/40 rounded p-3">
            La cuenta de HitTCG aún no está configurada por el admin. Contáctanos antes de transferir.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <CopyableField label="Banco" value={bank.bank_name} testid="hittcg-bank" />
            <CopyableField label="Tipo" value={(bank.account_type || "").toUpperCase()} testid="hittcg-type" />
            <CopyableField label="N° cuenta" value={bank.account_number} testid="hittcg-account" />
            <CopyableField label="Titular" value={bank.holder_name} testid="hittcg-holder" />
            <div className="sm:col-span-2">
              <CopyableField label="RUC / Cédula titular" value={bank.holder_id} testid="hittcg-id" />
            </div>
            {bank.instructions && (
              <div className="sm:col-span-2 text-xs text-neutral-400 bg-[#0A0A0A] border border-[#262626] rounded p-2">
                {bank.instructions}
              </div>
            )}
          </div>
        )}
        <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded text-xs text-blue-200 leading-relaxed">
          💡 <b>Monto exacto:</b> transfiere <b className="text-white">${Number(order.total).toFixed(2)}</b> — cualquier centavo de diferencia bloquea la confirmación automática.
          {hittcgIsPichincha && <> <br/>Si transfieres desde un banco distinto a <b>Pichincha</b>, tu banco puede cobrarte una comisión interbancaria de ~<b>${interbankFee.toFixed(2)}</b>. Ese costo lo asumes tú (no HitTCG).</>}
        </div>
      </div>

      <form onSubmit={submit} className="bg-[#111] border border-[#262626] rounded-md p-6 space-y-4">
        <h3 className="font-display text-lg">2. Sube el comprobante</h3>
        {showInterbankWarn && (
          <div className="text-xs bg-amber-500/10 border border-amber-500/40 rounded p-2 text-amber-200">
            Estás transfiriendo desde otro banco → considera el costo interbancario adicional (~${interbankFee.toFixed(2)}).
          </div>
        )}
        <div>
          <Label className="text-xs text-neutral-400 uppercase tracking-wider mb-2 block">Foto o PDF del comprobante</Label>
          <input
            data-testid="transfer-receipt-file"
            type="file"
            accept="image/*,.pdf"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="text-sm text-neutral-300 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-[#EF4444] file:text-white file:cursor-pointer"
          />
          {file && <div className="text-[11px] text-emerald-400 mt-1 flex items-center gap-1"><FileText size={12} />{file.name}</div>}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-neutral-400 uppercase tracking-wider mb-2 block">N° comprobante</Label>
            <Input
              data-testid="transfer-receipt-number"
              value={form.receipt_number}
              onChange={(e) => setForm(f => ({ ...f, receipt_number: e.target.value }))}
              required
              placeholder="Ej: 123456789"
              className="bg-[#171717] border-[#262626] font-mono"
            />
          </div>
          <div>
            <Label className="text-xs text-neutral-400 uppercase tracking-wider mb-2 block">Desde qué banco</Label>
            <BankSelect
              testId="transfer-bank-from"
              value={form.bank_from}
              onChange={(v) => setForm(f => ({ ...f, bank_from: v }))}
              required
            />
          </div>
          <div>
            <Label className="text-xs text-neutral-400 uppercase tracking-wider mb-2 block">Monto transferido</Label>
            <Input
              data-testid="transfer-amount"
              type="number"
              step="0.01"
              min="0"
              value={form.amount_reported}
              onChange={(e) => setForm(f => ({ ...f, amount_reported: e.target.value }))}
              required
              className="bg-[#171717] border-[#262626] font-mono"
            />
            <div className="text-[11px] text-neutral-500 mt-1">Total del pedido: <b className="text-white">${Number(order.total).toFixed(2)}</b></div>
          </div>
        </div>
        <Button type="submit" disabled={busy || !file} className="w-full sm:w-auto bg-[#EF4444] hover:bg-[#DC2626]" data-testid="transfer-submit-receipt">
          <Upload size={16} className="mr-2" />
          <BusyLabel busy={busy} text="Subiendo comprobante…">Enviar comprobante</BusyLabel>
        </Button>
      </form>
    </div>
  );
}

function TransferVerifyingBanner({ order, isBuyer }) {
  const r = order.transfer_receipt || {};
  return (
    <div className="bg-purple-500/10 border border-purple-500/40 rounded-md p-6 space-y-3" data-testid="transfer-verifying-banner">
      <div className="flex items-center gap-2 text-purple-200">
        <Clock size={18} />
        <h2 className="font-display text-xl">{isBuyer ? "Tu pago está en revisión" : "Pago en revisión"}</h2>
      </div>
      <p className="text-sm text-neutral-300 leading-relaxed">
        {isBuyer
          ? <>Espera a que lo verifiquemos; te avisamos cuando se apruebe. <b>La carta queda reservada para ti</b>: nadie más puede comprarla mientras revisamos. Puede tardar unas horas. Cuando confirmemos, el pedido pasa a <b>Pagado</b> y el vendedor la envía.</>
          : <>El comprador pagó por transferencia y la carta está <b>reservada</b>. HitTCG está verificando el depósito. Cuando confirme, recibirás la notificación de venta. No la vendas por otro lado.</>}
      </p>
      {r.receipt_number && (
        <div className="text-xs bg-[#0A0A0A] border border-[#262626] rounded p-3 space-y-1" data-testid="transfer-receipt-summary">
          <div className="text-neutral-500 uppercase tracking-wider text-[10px] mb-1">Comprobante enviado</div>
          <div><span className="text-neutral-500">N°:</span> <b className="text-white font-mono">{r.receipt_number}</b></div>
          <div><span className="text-neutral-500">Desde:</span> <b className="text-white">{r.bank_from}</b></div>
          <div><span className="text-neutral-500">Monto reportado:</span> <b className="text-white">${Number(r.amount_reported || 0).toFixed(2)}</b></div>
          {r.uploaded_at && <div className="text-neutral-500 text-[11px] mt-1">Subido: {new Date(r.uploaded_at).toLocaleString("es-EC")}</div>}
        </div>
      )}
    </div>
  );
}
